import React, { useEffect, useState } from "react";
import { debug, hasErrors } from "../utils";
import { connect } from "react-redux";
import * as validationService from "../devices/add-device-modal/validationService";
import * as authService from "./authService";
import { UserCardInput } from "./UserCardInput";
import { UserCardLogo } from "./UserCardLogo";
import UserRoleField from "./UserRoleField";
import { CardTypes, UserCardService } from "./UserCardService";
import { getImage, getUserById } from "./authService";
import { Spinner } from "../view/Spinner";
import * as groupService from "../devices/add-device-modal/groupService";
import UserDeviceGroupsField from "./UserDeviceGroupsField";
import { PasswordComplexityUsage } from "../system/system-settings/PasswordComplexityUsage";
import { PasswordComplexityHint } from "../system/system-settings/PasswordComplexityHint";

const localUser = 'local';
const externalUser = 'external';

function UserCard({ errors, handleClose, cardType = CardTypes.CREATE, userId, loggedUserId }) {

    const { validatePassword } = PasswordComplexityUsage();
    const [passwordErrors, setPasswordErrors] = useState([]);

    const valid = !hasErrors(errors);
    const isCreateMode = cardType === CardTypes.CREATE;
    const formId = "oct-auth-user-form";

    const [loading, setLoading] = useState(true);
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [email, setEmail] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [image, setImage] = useState();
    const [imagePreview, setImagePreview] = useState(null);

    const [passwordChanged, setPasswordChanged] = useState(false);
    const [imageChanged, setImageChanged] = useState(false);

    const [adUser, setAdUser] = useState(false);
    const [userType, setUserType] = useState('local');
    const [passwordVisible, setPasswordVisible] = useState(true);

    const [userRoles, setUserRoles] = useState("");
    const [userRoleId, setUserRoleId] = useState("-1");
    const [deviceGroups, setDeviceGroups] = useState([]);
    const [selectedGroupIds, setSelectedGroupIds] = useState([]);

    const [enableOtp, setEnableOtp] = useState(false);

    const userCardService = new UserCardService(cardType, handleClose, loggedUserId);

    const fetchData = async () => {
        try {
            const userRoleList = await authService.listRoles();
            setUserRoles(userRoleList);
            const groupList = await groupService.list();
            setDeviceGroups(groupList || []);

            if (userId && cardType === CardTypes.EDIT) {
                const user = await getUserById(userId);
                if (user) {
                    setUsername(user.username);
                    setFirstName(user.firstName);
                    setLastName(user.lastName);
                    setEmail(user.email);
                    setUserRoleId(user.userRole.pk);
                    setEnableOtp(user.enableOtp);
                    setSelectedGroupIds((user.groups || []).map(group => group.id || group.pk));

                    if (user.type === externalUser) {
                        setUserType(externalUser);
                        setPassword(externalUser);
                        setAdUser(true);
                        setPasswordVisible(false);
                    } else {
                        setUserType(localUser);
                        setPassword('******');
                        setAdUser(false);
                        setPasswordVisible(true);
                    }

                    if (user.logoUrl) {
                        setImagePreview(getImage(user.logoUrl));
                    }
                }
            } else {
                validationService.validateNotEmpty(username, 'username');
                validationService.validateNotEmpty(password, 'password');
                validationService.validateNotEmpty(firstName, 'firstName');
                validationService.validateNotEmpty(lastName, 'lastName');
                validationService.validateNotEmpty(email, 'email');
                validationService.validateRoleSelected("", 'userRole');
            }

            setLoading(false);
        } catch (error) {
            //console.error('Error:', error);
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();

        return () => {
            validationService.cleanState();
        };
        /* eslint-disable react-hooks/exhaustive-deps */
    }, []);

    const onChange = (value) => {
        setAdUser(value);
        setPasswordVisible(!value);
        if (value) {
            setUserType(externalUser);
            setPassword(externalUser);
            validationService.validateNotEmpty(value, 'password');
        } else {
            setUserType(localUser);
            setPassword("");
            validationService.validateNotEmpty(value, 'password');
        }
    }

    const onChangeOtp = (value) => {
        setEnableOtp(value);
    }

    const onUsernameChange = (value) => {
        validationService.validateNotEmpty(value, 'username');
        setUsername(value);
    }

    const onPasswordChange = (value) => {
        validationService.validateNotEmpty(value, 'password');
        setPassword(value);
        setPasswordChanged(true);
        const errors = validatePassword(value);
        setPasswordErrors(errors);
    }


    const onFirstNameChange = (value) => {
        validationService.validateNotEmpty(value, 'firstName');
        setFirstName(value);
    }

    const onEmailChange = (value) => {
        validationService.validateNotEmpty(value, 'email');
        setEmail(value);
    }

    const onLastNameChange = (value) => {
        validationService.validateNotEmpty(value, 'lastName');
        setLastName(value);
    }

    const onPasswordFocus = () => {
        if (!passwordChanged) {
            setPassword('');
            setPasswordChanged(true);
        }
    }

    const onUserRoleChange = (value) => {
        let selected = ""
        if (value !== "-1") selected = value;
        validationService.validateRoleSelected(selected, 'userRole');
        setUserRoleId(value);
    }

    const onClearImgClick = () => {
        setImage(null);
        setImagePreview(null);
        setImageChanged(true);
    }

    const onImgChange = (event) => {
        const file = event.target.files[0];

        if (!file) {
            return;
        }

        setImage(file);

        const reader = new FileReader();
        reader.onload = () => {
            setImagePreview(reader.result);
        };
        reader.readAsDataURL(file);

        event.target.type = '';
        event.target.type = 'file';
        setImageChanged(true);
    }

    //const isPasswordValid = () => password && password.length >= 6;
    const isPasswordValid = () => password && password.length >= 6 && passwordErrors.length === 0;
    const canSubmit = valid && (!passwordVisible || isPasswordValid());
    const selectedUserRole = Array.isArray(userRoles)
        ? userRoles.find(role => String(role.id || role.pk) === String(userRoleId))
        : null;
    const isAdminRoleSelected = String(selectedUserRole?.name || "").toLowerCase() === "admin";

    useEffect(() => {
        if (isAdminRoleSelected && selectedGroupIds.length) {
            setSelectedGroupIds([]);
        }
    }, [isAdminRoleSelected, selectedGroupIds.length]);

    return (
        <>
            <div className="modal-body modal-v2-body oct_auth-user-modal-body">
                {loading ? (
                    <div className="oct_auth-user-modal-loading">
                        <Spinner />
                    </div>
                ) : (
                    <form id={formId} className="oct_auth-user-form modal-v2-form" autoComplete="off" onSubmit={(event) => {
                        event.preventDefault();
                        if (!canSubmit) {
                            return;
                        }

                        const payload = {
                            username,
                            password,
                            firstName,
                            lastName,
                            image,
                            email,
                            passwordChanged,
                            imageChanged,
                            id: userId,
                            userType,
                            userRoleId,
                            groupIds: selectedGroupIds,
                            enableOtp
                        };

                        if (isCreateMode) {
                            userCardService.onCreate(payload);
                            return;
                        }

                        userCardService.onSave(payload);
                    }}>
                        <div className="oct_auth-user-profile-section">
                            <UserCardLogo
                                imgPreview={imagePreview}
                                onChange={onImgChange}
                                onClear={onClearImgClick}
                            />
                        </div>

                        <div className="oct_auth-user-form-grid">
                            <UserCardInput
                                label={'Username'}
                                value={username}
                                onChange={onUsernameChange}
                                valid={username}
                                required
                            />

                            <UserCardInput
                                label={'Email'}
                                value={email}
                                onChange={onEmailChange}
                                valid={email}
                                required
                            />

                            <UserCardInput
                                label={'First Name'}
                                value={firstName}
                                onChange={onFirstNameChange}
                                valid={firstName}
                                required
                            />

                            <UserCardInput
                                label={'Last Name'}
                                value={lastName}
                                onChange={onLastNameChange}
                                valid={lastName}
                                required
                            />

                            {passwordVisible && (
                                <>
                                <UserCardInput
                                    label={'Password'}
                                    value={password}
                                    type={'password'}
                                    onFocus={() => onPasswordFocus()}
                                    onChange={onPasswordChange}
                                    valid={isPasswordValid()}
                                    required
                                />
                                <PasswordComplexityHint errors={passwordErrors} />
                                </>
                                
                            )}

                            <UserRoleField
                                userRoles={userRoles}
                                userRoleId={userRoleId}
                                onChange={onUserRoleChange}
                                valid={userRoleId !== "-1"}
                                required
                            />

                            {!isAdminRoleSelected && (
                                <UserDeviceGroupsField
                                    groups={deviceGroups}
                                    selectedGroupIds={selectedGroupIds}
                                    onChange={setSelectedGroupIds}
                                />
                            )}
                        </div>

                        <div className="oct_auth-user-toggle-grid">
                            <div className="oct_check oct_auth-user-check">
                                <label className="oct_check-label oct_auth-user-check-label">
                                    <input type="checkbox" checked={!!adUser} onChange={() => onChange(!adUser)} />
                                    <span>External User</span>
                                </label>
                            </div>

                            {username !== 'admin' && (
                                <div className="oct_check oct_auth-user-check">
                                    <label className="oct_check-label oct_auth-user-check-label">
                                        <input type="checkbox" checked={!!enableOtp} onChange={() => onChangeOtp(!enableOtp)} />
                                        <span>Enable OTP</span>
                                    </label>
                                </div>
                            )}
                        </div>
                    </form>
                )}
            </div>

            <div className="modal-footer modal-v2-footer oct_auth-user-modal-footer">
                <div className="oct_auth-user-footer-copy">
                    <div className="modal-v2-footer-note">
                        <span className="modal-v2-footer-note-star">*</span> Required fields
                    </div>
                    {userId === loggedUserId && (
                        <div className="oct_auth-user-footer-warning">
                            Saving your own account will sign you out.
                        </div>
                    )}
                </div>
                <div className="modal-v2-footer-actions oct_auth-user-modal-actions">
                    <button type="button" className="btn modal-v2-btn modal-v2-btn-outline oct_auth-user-modal-btn" onClick={handleClose}>
                        Cancel
                    </button>
                    <button
                        type="submit"
                        form={formId}
                        className="btn modal-v2-btn modal-v2-btn-primary oct_auth-user-modal-btn"
                        disabled={loading || !canSubmit}
                    >
                        {isCreateMode ? "Create" : "Save"}
                    </button>
                </div>
            </div>
        </>
    )
}

function mapStateToProps(state) {
    debug("UserCard mapStateToProps:", state);
    const validation = state.validation;
    const loggedUserId = state.auth.id || null;
    return {
        errors: validation || {},
        loggedUserId: loggedUserId
    }
}

export default connect(mapStateToProps)(UserCard);
